// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Utility functions for working with generic schema-generated analysis data.
 * Works with any schema structure generated by the Schema Generator.
 */

import type { AIAnalysis, AIImpact, MonitorImpactType } from '@/types/workspace-bill'
import type { Config } from '@/api/types/config'

/**
 * Extract filter score configuration from config object
 */
export function getFilterScoreConfig(config?: Config | null): {
  filterScoreSection: string
  filterScoreField: string
} {
  return {
    filterScoreSection: config?.filter_score_section || 'summary',
    filterScoreField: config?.filter_score_field || 'overall_impact'
  }
}

/**
 * Get filter score from schema-generated analysis data
 * Always looks for filter score in summary.filter_score
 */
export function getFilterScore(analysis?: AIAnalysis): AIImpact | undefined {
  if (!analysis) return undefined
  
  // Always look for filter score in summary.filter_score
  const summary = analysis.summary
  if (summary && typeof summary === 'object') {
    const filter_score = summary.filter_score
    
    if (filter_score && typeof filter_score === 'object') {
      const score = filter_score.score
      const score_type = filter_score.score_type
      
      if ((typeof score === 'number' || typeof score === 'string') && typeof score_type === 'string') {
        // Parse score to number if it's a string
        const numericScore = typeof score === 'string' ? parseFloat(score) : score
        
        // Validate that the parsed score is a valid number
        if (!isNaN(numericScore)) {
          // Validate score_type is a valid MonitorImpactType
          const validTypes = ['positive', 'negative', 'neutral']
          const validatedType = validTypes.includes(score_type) ? score_type as MonitorImpactType : 'neutral'
          
          return {
            score: numericScore,
            type: validatedType
          }
        }
      }
    }
  }
  
  return undefined
}

/**
 * Get monitoring status from schema-generated analysis data
 * Looks for boolean fields that might indicate monitoring status
 */
export function getShouldMonitor(analysis?: AIAnalysis): boolean {
  if (!analysis) return false
  
  // Look for common monitoring indicators across all sections
  const sections = Object.keys(analysis).filter(key => key !== 'title')
  
  for (const sectionKey of sections) {
    const section = analysis[sectionKey]
    if (section && typeof section === 'object') {
      // Check for explicit monitoring fields
      if (section.should_monitor === true) return true
      if (section.monitor === true) return true
      
      // Check impact category for monitoring indicators
      if (section.impact_category) {
        const category = String(section.impact_category).toLowerCase()
        if (category.includes('direct') || category.includes('significant')) {
          return true
        }
      }
    }
  }
  
  return false
}

/**
 * Get analysis section by key (generic for any schema structure)
 */
export function getAnalysisSection(analysis?: AIAnalysis, sectionKey?: string): any {
  if (!analysis || !sectionKey) return undefined
  return analysis[sectionKey]
}

/**
 * Get score from analysis section (supports direct integer fields)
 */
export function getScore(section: any, scoreType: string): AIImpact | undefined {
  if (!section) return undefined
  
  // Try direct field access for integer values
  const directField = section[scoreType]
  if (typeof directField === 'number') {
    return {
      score: directField,
      type: 'neutral'
    }
  }
  
  return undefined
}

/**
 * Get rationale from analysis section (generic for any schema structure)
 */
export function getRationale(section: any): string | undefined {
  if (!section) return undefined
  return section.rationale || section.reason || section.explanation
}

/**
 * Get all sections from analysis data ordered by their order property
 */
export function getOrderedSections(analysis?: AIAnalysis): Array<{key: string, section: any}> {
  if (!analysis) return []
  
  const sections = Object.keys(analysis)
    .filter(key => key !== 'title')
    .map(key => ({
      key,
      section: analysis[key],
      order: analysis[key]?.order || 999
    }))
    .sort((a, b) => a.order - b.order)
  
  return sections
}